import { logger } from "@lib/logger"
import { isAxiosError } from "axios"

/**
 * Reading an error code off a failed API call — one implementation.
 *
 * Every mutation in this app wrote the same three lines by hand:
 *
 *     const code = apiErrorCode(err)
 *     toast.error(t(`errors:${code}`, t("errors:internal_error")))
 *
 * 76 `isAxiosError` call sites, 29 `response?.data?.detail` unwraps, and 15
 * of the toast lines were the identical expression character for character.
 * That made an error-contract change a ~50-site edit, and it meant nine
 * places hand-cast the response because no shared payload type existed
 * (audit 2026-09-07, F-FE-2).
 *
 * The backend's half of this contract is deliberate and worth knowing: every
 * `HttpError` returns a snake_case CODE, never a sentence, and
 * `api/schemas.py` documents that each one "maps 1:1 to the frontend's
 * `errors:<code>` i18n namespace". So the only job here is to get the code
 * out safely — the copy lives in the locale files.
 */

/** The shape the backend's error handler returns. */
interface ApiErrorBody {
  detail?: unknown
}

/**
 * The backend's error code for a failed request, or `null`.
 *
 * `null` for a network failure, a timeout, a non-Axios throw, or a body that
 * does not carry a string `detail` — all cases where there is no code to
 * translate and the caller should fall back to its own generic message.
 */
export function apiErrorCode(err: unknown): string | null {
  if (!isAxiosError(err)) return null
  const detail = (err.response?.data as ApiErrorBody | undefined)?.detail
  return typeof detail === "string" && detail.length > 0 ? detail : null
}

/**
 * The i18n key for an error, with a fallback key when there is no code.
 *
 * Returns a `[key, fallbackKey]` pair for `t()`'s two-argument form, so an
 * unmapped code degrades to a readable sentence instead of rendering the raw
 * code — a teacher saw `task_index_mismatch` in production on 2026-08-04.
 */
export function apiErrorKeys(err: unknown, fallback = "internal_error"): [string, string] {
  const code = apiErrorCode(err) ?? fallback
  return [`errors:${code}`, `errors:${fallback}`]
}

/**
 * A request made with `responseType: "blob"` receives its ERROR body as a
 * Blob too, so `apiErrorCode` finds no `detail` and a precise 422 such as
 * `cut_out_of_range` degrades to "Algo no funcionó de nuestro lado". This
 * re-reads that body as JSON, in place, and hands the same error back for the
 * caller to throw — after it, the normal `apiErrorCode` path works.
 */
export async function withJsonErrorBody(err: unknown): Promise<unknown> {
  if (!isAxiosError(err) || !err.response || !(err.response.data instanceof Blob)) return err
  try {
    err.response.data = JSON.parse(await err.response.data.text()) as unknown
  } catch {
    // Not JSON (a proxy's HTML error page, an empty body): the caller's
    // fallback copy applies, and the status still says what happened.
    logger.warning("api.blob_error_body_unreadable", { status: err.response.status })
  }
  return err
}
